import { withTranslation } from '@application/i18n/with_translation';
import { FileViewer, Spinner, Text, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';
import * as RovingFocusGroup from '@radix-ui/react-roving-focus';
import { CaseResourceDto } from '@visiba/api-vcs-client-v2';
import { usePresentation } from '@visiba/presentation';
import { rem } from '@visiba/std';
import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ChatAutoScroller } from './_chat_auto_scroller.component';
import { ChatBubble } from './_chat_bubble.component';
import { NewMessagesBanner } from './_chat_new_messages_banner.component';
import { ReadUntilContainer } from './_read_until_container.component';
import type { ChatCommunicator } from './chat_communicator';
import type { ControlledChatMessage, ControlledMessage, ControlledSystemMessage } from './common';

interface Props {
	showLoader: boolean;
	bridge: ChatCommunicator | null;
	markAsCompleted: boolean;
	topBanner: ReactNode | undefined;
	bottomBanner: ReactNode | undefined;
	onMarkAsCompleted?: () => void;
	onReceivedHistory?: () => void;
	messagingMetaData?: MessagingMetaData | null;
}

const elements = {
	start: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: ${tokens.spacing.x2} 0;
	`,
	systemMessage: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: ${tokens.spacing.x3} 0;
	`,
	end: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: ${tokens.spacing.x6} 0 ${tokens.spacing.x8} 0;
	`,
	container: styled.div`
		display: flex;
		flex: 1;
		overflow: hidden;
		position: relative;
		width: 100%;
		min-height: 150px;
		flex-direction: column;
		mask-image: linear-gradient(to top, transparent 0px, #000 4px);
		-webkit-mask-image: linear-gradient(to top, transparent 0px, #000 4px);
	`,
	senderInfo: styled.div`
		margin-top: ${rem(16)};
		font-size: ${rem(14)};
		line-height: ${rem(16)};
	`,
	loader: styled.div`
		z-index: 1;
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background: #ffffff9c;
		z-index: 1;
		display: flex;
		justify-content: center;
		align-items: center;
		backdrop-filter: blur(2px);
	`,
};

export function ChatMessagesContainer({
	bridge,
	showLoader,
	bottomBanner,
	topBanner,
	onMarkAsCompleted,
	onReceivedHistory,
	messagingMetaData,
}: Props): JSX.Element {
	const [hasReceivedHistory, setHasReceivedHistory] = useState(false);

	useEffect(() => {
		if (bridge == null) return;

		setHasReceivedHistory(bridge.presentation.read().hasReceivedHistory);

		// This dirty logic is needed since the code is currently made so bridge
		// is potentially null in the component's scope but is still dependent
		// on its state. TODO: Will change this.
		return bridge.presentation.subscribe((set) => {
			if (set?.has('hasReceivedHistory')) {
				const { hasReceivedHistory } = bridge.presentation.read();
				setHasReceivedHistory(hasReceivedHistory);

				if (hasReceivedHistory && onReceivedHistory != null) {
					// FIX ME: without the tick, it's an infinite loop...
					setTimeout(() => {
						onReceivedHistory();
					});
				}
			}

			if (set?.has('isCompleted')) {
				const { isCompleted } = bridge.presentation.read();
				if (isCompleted && onMarkAsCompleted != null) {
					onMarkAsCompleted();
				}
			}
		});
	}, [bridge, onMarkAsCompleted, onReceivedHistory]);

	return (
		<elements.container>
			{hasReceivedHistory && bridge != null ? (
				<Messages bridge={bridge} bottomBanner={bottomBanner} topBanner={topBanner} resourceInfo={messagingMetaData?.resourceInfo} />
			) : null}
			{showLoader ? (
				<elements.loader>
					<Spinner size='large' />
				</elements.loader>
			) : null}
		</elements.container>
	);
}

interface MessagesProps {
	bridge: ChatCommunicator;
	topBanner: ReactNode | undefined;
	bottomBanner: ReactNode | undefined;
	resourceInfo?: Nullable<CaseResourceDto>;
}

function getMessageIndexToRenderReceipt(messages: ControlledMessage[], resourceInfo?: Nullable<CaseResourceDto>) {
	return messages.findLastIndex((message) => {
		if (message.type === 'chatMessage') {
			if (message.peerReadTimestamp && message.peerReadFullName) {
				return true;
			} else if (resourceInfo?.HasReadUntilTimestamp) {
				return message.isMe && message.timestamp.isBefore(resourceInfo.HasReadUntilTimestamp);
			}
		}

		return false;
	});
}

function getPeerReadTimestamp(message: ControlledChatMessage, resourceInfo?: Nullable<CaseResourceDto>) {
	const peerReadTimestamp = message.peerReadTimestamp ?? resourceInfo?.HasReadUntilTimestamp;

	return peerReadTimestamp ?? null;
}

function getPeerReadFullName(message: ControlledChatMessage, resourceInfo?: Nullable<CaseResourceDto>) {
	if (message.peerReadFullName) {
		return message.peerReadFullName;
	}

	if (resourceInfo?.FirstName && resourceInfo.LastName) {
		return `${resourceInfo.FirstName} ${resourceInfo.LastName}`;
	}

	return;
}

function Messages({ bridge, bottomBanner, topBanner, resourceInfo }: MessagesProps): JSX.Element {
	const presentation = usePresentation(bridge.presentation);

	function handleMessageShown(message: ControlledChatMessage | ControlledSystemMessage): void {
		bridge.setReadUntil(message);
	}

	return (
		<FileViewer.Root>
			<ChatAutoScroller>
				<RovingFocusGroup.Root orientation='vertical' role='list' aria-labelledby='chat_messages_container'>
					<VisuallyHidden id='chat_messages_container'>{withTranslation('PW.Component.Chat.A11y.ChatRegion')}</VisuallyHidden>

					{topBanner != null ? (
						<RovingFocusGroup.Item asChild>
							<elements.start>{topBanner}</elements.start>
						</RovingFocusGroup.Item>
					) : null}

					{(() => {
						// This should maybe be derived from the controller rather than be set locally here. A bit hacky with the IIFE hehe.
						let hasHadNewTimeStamp = false;

						const messageIndexToRenderReceipt = getMessageIndexToRenderReceipt(presentation.messages, resourceInfo);

						return presentation.messages.map((message, index): JSX.Element => {
							const isLastMessage = presentation.messages.length - 1 === index;
							const isDefaultActiveInRovingFocus = hasHadNewTimeStamp ? false : isLastMessage;
							const isMessageWithReceipt = messageIndexToRenderReceipt === index;

							switch (message.type) {
								case 'chatMessage': {
									const previousMessage = presentation.messages[index - 1] as ControlledMessage | undefined; // Casting to not implicitly assert non-nullish.
									const nextMessage = presentation.messages[index + 1] as ControlledMessage | undefined; // Casting to not implicitly assert non-nullish.
									const isNewSender = previousMessage?.fromId !== message.fromId;
									const isMessageStreak = previousMessage?.fromId === message.fromId;
									const isLastMessageInStreak = nextMessage?.fromId !== message.fromId;

									// read receipt props
									const peerReadTimestamp = isMessageWithReceipt ? getPeerReadTimestamp(message, resourceInfo) : undefined;
									const peerReadFullName = isMessageWithReceipt ? getPeerReadFullName(message, resourceInfo) : undefined;

									return (
										<RovingFocusGroup.Item key={message.id} asChild active={isDefaultActiveInRovingFocus}>
											<ChatBubble
												message={message}
												onRead={handleMessageShown}
												data-is-me={message.isMe}
												isNewSender={isNewSender}
												isMessageStreak={isMessageStreak}
												isLastMessageInStreak={isLastMessageInStreak}
												windowHasFocus={presentation.windowHasFocus}
												peerReadTimestamp={peerReadTimestamp}
												peerReadFullName={peerReadFullName}
											/>
										</RovingFocusGroup.Item>
									);
								}
								case 'systemMessage': {
									return (
										<RovingFocusGroup.Item key={message.id} asChild active={isDefaultActiveInRovingFocus} data-is-me={false}>
											<elements.systemMessage>
												<Text
													variant='bodySmallRegular'
													style={{
														color: tokens.color.content.muted,
													}}
												>
													{message.text}
												</Text>
												{message.hasBeenRead ? null : (
													<ReadUntilContainer
														onVisible={() => handleMessageShown(message)}
														shouldBeObserved={presentation.windowHasFocus}
													/>
												)}
											</elements.systemMessage>
										</RovingFocusGroup.Item>
									);
								}
								case 'unreadTimestampMessage': {
									// The mutation here is crucial just to make the default roving list logic work.
									hasHadNewTimeStamp = true;

									return (
										<RovingFocusGroup.Item key={message.id} asChild active>
											<NewMessagesBanner />
										</RovingFocusGroup.Item>
									);
								}
							}
						});
					})()}
					{bottomBanner != null ? (
						<RovingFocusGroup.Item asChild>
							<elements.end>{bottomBanner}</elements.end>
						</RovingFocusGroup.Item>
					) : null}
				</RovingFocusGroup.Root>
			</ChatAutoScroller>
		</FileViewer.Root>
	);
}
